import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SHORTCUT_MAP, ShortcutEntry } from '../../hooks/keyboardShortcuts.constants';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
  shortcuts?: ShortcutEntry[];
}

const categoryLabels: Record<string, string> = {
  navigation: 'Navigation',
  actions: 'Actions',
  selection: 'Selection',
  pagination: 'Pagination',
};

const KeyboardShortcutsHelp = ({ open, onClose, shortcuts }: KeyboardShortcutsHelpProps) => {
  const entries = shortcuts ?? SHORTCUT_MAP;
  const grouped = entries.reduce<Record<string, ShortcutEntry[]>>(
    (acc, entry) => {
      if (!acc[entry.category]) acc[entry.category] = [];
      acc[entry.category].push(entry);
      return acc;
    },
    {},
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableRestoreFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Keyboard Shortcuts
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {Object.entries(grouped).map(([category, entries]) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}
            >
              {categoryLabels[category] || category}
            </Typography>
            {entries.map(entry => (
              <Box
                key={entry.keys}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.5,
                }}
              >
                <Typography variant="body2">{entry.description}</Typography>
                <Box
                  component="kbd"
                  sx={{
                    px: 1,
                    py: 0.25,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    backgroundColor: 'action.hover',
                    minWidth: 28,
                    textAlign: 'center',
                  }}
                >
                  {entry.label}
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsHelp;
